"use client"

/**
 * Step 5 — Fund allocation (IBA-170). The final, mandatory onboarding step.
 *
 * WHY THIS SCREEN EXISTS, in the words the user needs. The previous steps recorded WHERE the church's money
 * is: so much in the bank, so much in the cash box. They did not record what any of it is FOR. Every expense
 * in Ibada Cloud is paid out of a fund, so until the opening money is divided across funds, the branch has a
 * balance it is structurally unable to spend. That is why this step cannot be skipped, and why the wording
 * here explains the reason rather than just demanding a number.
 *
 * THE TOTALS ARE THE SERVER'S. Total, allocated and remaining all come from the ledger via the state query.
 * The client re-derives `remaining` only while the user is typing, for immediate feedback; the button that
 * matters is gated on the SERVER's figure after a save. A screen that said "Remaining: 0" while the backend
 * disagreed would let a treasurer press Complete and be refused — the one failure this step exists to prevent.
 */

import * as React from "react"
import { CheckCircle2Icon, InfoIcon, PiggyBankIcon } from "lucide-react"

import { Button, HelperMessage, Label, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import { CurrencyAmountInput } from "@/shared/components/currency-amount-input"
import {
  useOpeningFundAllocationsQuery,
  useSetOpeningFundAllocationsMutation,
} from "@/features/finance/services/opening-fund-allocations-api"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileAmountInput, useFormatMoneyText } from "@/shared/components/mobile-form"
import { cn } from "@ibada/ui"

/** Money maths in integer cents — a running total built from floats drifts visibly across a dozen funds. */
const toCents = (value: string): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
}

const fromCents = (cents: number): string => (cents / 100).toFixed(2)

/**
 * Does what is on screen account for the whole opening balance?
 *
 * `loaded` is not decoration. The totals it is called with are derived before the step's loading guard, so an
 * unloaded step offers 0 against 0 — which subtracts to zero and reads as balanced. The wizard enables
 * Complete on this answer, so saying "balances" about figures nobody has yet put a treasurer in front of a
 * live button the server was always going to refuse.
 */
export const isAllocationDraftBalanced = (loaded: boolean, totalCents: number, draftCents: number): boolean =>
  loaded && totalCents - draftCents === 0

export function OnboardingFundAllocationStep({
  workspaceId,
  branchId,
  canEdit,
  onDraftStateChange,
  saveRef,
}: {
  workspaceId: string
  branchId: string
  canEdit: boolean
  /**
   * What the user is looking at right now, so the wizard can say the right thing about its disabled button.
   * The SERVER still decides whether Complete is enabled — this only decides the WORDING, because "allocate
   * everything" is wrong advice for someone who already has and just needs to press Save.
   */
  onDraftStateChange?: (state: { balanced: boolean; dirty: boolean; resolved: boolean }) => void
  /** Holds this step's save so the wizard's single Complete button can persist the draft before activating. */
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}) {
  const t = useTranslations("finance.onboarding.fundAllocation")

  const stateQuery = useOpeningFundAllocationsQuery(workspaceId, branchId)
  const saveAllocations = useSetOpeningFundAllocationsMutation(workspaceId, branchId)

  const state = stateQuery.data ?? null
  const currency = state?.currency ?? "USD"
  const isMobile = useIsMobile()
  const formatMoneyText = useFormatMoneyText(currency)

  /** Draft amounts keyed by fund id. Seeded from the server once, then owned by the user until saved. */
  const [draft, setDraft] = React.useState<Record<string, string>>({})
  const seededFor = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!state) return
    // Re-seed when the branch changes or after a save, but never mid-edit — that would erase what the user
    // is typing every time the query refetched in the background.
    const signature = `${state.branchId}:${state.funds.length}:${state.allocated}`
    if (seededFor.current === signature) return
    seededFor.current = signature
    // A zero seeds as EMPTY, not `0.00`: nothing has been allocated to that fund yet, and a pre-filled figure
    // is something the reader must select and delete before they can type the amount they actually mean.
    setDraft(Object.fromEntries(state.funds.map((fund) => [fund.id, toCents(fund.amount) === 0 ? "" : fund.amount])))
  }, [state])

  const totalCents = toCents(state?.totalOpeningMoney ?? "0")
  const draftCents = Object.values(draft).reduce((sum, value) => sum + toCents(value || "0"), 0)
  const remainingCents = totalCents - draftCents

  const dirty = React.useMemo(() => {
    if (!state) return false
    return state.funds.some((fund) => toCents(draft[fund.id] ?? "0") !== toCents(fund.amount))
  }, [state, draft])

  const balancedDraft = isAllocationDraftBalanced(stateQuery.isSuccess, totalCents, draftCents)
  React.useEffect(() => {
    onDraftStateChange?.({ balanced: balancedDraft, dirty, resolved: stateQuery.isSuccess })
  }, [balancedDraft, dirty, onDraftStateChange, stateQuery.isSuccess])

  /**
   * Persist the draft. Exposed to the wizard through `saveRef` rather than owned by a button of its own:
   * "Save allocation" and "Complete finance setup" were two presses for one intention, and the second sat
   * disabled until the first had been found — so Complete now saves before it activates.
   */
  const handleSave = React.useCallback(async () => {
    if (!state) return
    await saveAllocations.mutateAsync({
      allocations: state.funds
        .map((fund) => ({ fundId: fund.id, amount: fromCents(toCents(draft[fund.id] ?? "0")) }))
        .filter((entry) => toCents(entry.amount) !== 0),
    })
  }, [state, draft, saveAllocations])

  // No dep array: `handleSave` closes over the live draft, so the wizard must always hold the current one.
  React.useEffect(() => {
    if (!saveRef) return
    saveRef.current = dirty ? handleSave : null
    return () => {
      saveRef.current = null
    }
  })

  /** Put everything still unallocated on one fund — the common case is a single general-purpose fund. */
  const assignRemainder = (fundId: string) => {
    setDraft((current) => ({
      ...current,
      [fundId]: fromCents(toCents(current[fundId] ?? "0") + remainingCents),
    }))
  }

  if (stateQuery.isLoading) {
    return (
      <div className='space-y-6' aria-label={t("loading")}>
        <Skeleton className='h-16 w-full rounded-lg' />
        <div className='grid gap-px overflow-hidden rounded-lg border p-3 sm:grid-cols-3'>
          <Skeleton className='h-14 w-full' />
          <Skeleton className='h-14 w-full' />
          <Skeleton className='h-14 w-full' />
        </div>
        <div className='space-y-3 rounded-lg border p-3'>
          <Skeleton className='h-10 w-full' />
          <Skeleton className='h-10 w-full' />
        </div>
      </div>
    )
  }

  if (!state) return <p className='text-muted-foreground text-sm'>{t("unavailable")}</p>

  /* A branch that converted with no money has nothing to divide — IBA-170 says the step is satisfied as-is. */
  if (!state.hasOpeningMoney) {
    return (
      <HelperMessage tone='success' size='md' icon={CheckCircle2Icon}>
        <span className='font-medium'>{t("noMoneyTitle")}</span> {t("noMoneyBody")}
      </HelperMessage>
    )
  }

  if (state.funds.length === 0) {
    return (
      <HelperMessage tone='warning' size='md'>
        {t("noFunds")}
      </HelperMessage>
    )
  }

  const balanced = remainingCents === 0
  const overAllocated = remainingCents < 0

  // Phone: what is left to divide as the large figure (green once it reaches zero), the total and the part
  // already divided under it, then one grouped row per fund with its amount typed in place.
  if (isMobile) {
    return (
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col items-center gap-1 rounded-[1.5rem] bg-card px-4 py-5 text-center'>
          <span className='text-[13px] font-bold text-muted-foreground'>{t("totals.remaining")}</span>
          <span
            className={cn(
              "max-w-full break-all text-[2.25rem] leading-none font-extrabold tracking-tight tabular-nums",
              balanced ? "text-primary" : overAllocated ? "text-destructive" : "text-amber-600 dark:text-amber-400",
            )}
          >
            {formatMoneyText(fromCents(remainingCents))}
          </span>
          <span className='mt-1 text-xs font-semibold text-muted-foreground tabular-nums'>
            {t("totals.total")} {formatMoneyText(state.totalOpeningMoney)} · {t("totals.allocated")} {formatMoneyText(fromCents(draftCents))}
          </span>
        </div>

        <section className='flex flex-col'>
          <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-card'>
            {state.funds.map((fund) => (
              <div key={fund.id} className='flex min-h-14 items-center gap-3 px-4 py-2'>
                <span className='flex min-w-0 flex-1 flex-col items-start gap-1'>
                  <span className='w-full truncate text-[15px] font-bold'>{fund.name}</span>
                  {canEdit && state.isEditable && remainingCents > 0 ? (
                    <button
                      type='button'
                      onClick={() => assignRemainder(fund.id)}
                      className='rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-extrabold text-primary active:bg-primary/20'
                    >
                      {t("assignRemainder")}
                    </button>
                  ) : null}
                </span>
                <MobileAmountInput
                  value={draft[fund.id] ?? ""}
                  onChange={(value) => setDraft((current) => ({ ...current, [fund.id]: value }))}
                  currencyCode={currency}
                  ariaLabel={fund.name}
                  disabled={!canEdit || !state.isEditable}
                />
              </div>
            ))}
          </div>
          <p className='px-4 pt-1.5 text-xs font-semibold text-muted-foreground'>
            <span className='font-extrabold'>{t("whyTitle")}</span> {t("whyBody")}
          </p>
        </section>

        {overAllocated ? (
          <p className='px-4 text-[13px] font-semibold text-destructive'>
            {t("overAllocated", { amount: formatMoneyText(fromCents(Math.abs(remainingCents))) })}
          </p>
        ) : null}
        {!state.isEditable ? <p className='px-4 text-[13px] font-semibold text-muted-foreground'>{t("locked")}</p> : null}
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* The contextual help IBA-170 asks for: WHY this is required, not just what to type. */}
      <HelperMessage tone='info' size='md' icon={InfoIcon}>
        <span className='font-medium'>{t("whyTitle")}</span> {t("whyBody")}
      </HelperMessage>

      {/* The three figures. Remaining is the one that decides whether setup can be completed. */}
      <div className='grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-3'>
        <Figure label={t("totals.total")} value={formatMoney(state.totalOpeningMoney, currency)} />
        <Figure label={t("totals.allocated")} value={formatMoney(fromCents(draftCents), currency)} />
        <Figure
          label={t("totals.remaining")}
          value={formatMoney(fromCents(remainingCents), currency)}
          tone={balanced ? "success" : overAllocated ? "destructive" : "warning"}
        />
      </div>

      {/* One compact row per fund — a list, not a stack of boxes. */}
      <div className='divide-y rounded-lg border'>
        {state.funds.map((fund) => (
          <div key={fund.id} className='flex flex-wrap items-center gap-3 px-3 py-2.5'>
            <PiggyBankIcon className='text-muted-foreground size-4 shrink-0' aria-hidden='true' />
            <Label htmlFor={`alloc-${fund.id}`} className='min-w-0 flex-1 truncate font-normal'>
              {fund.name}
            </Label>

            {canEdit && !balanced && remainingCents > 0 ? (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-7 px-2 text-xs'
                onClick={() => assignRemainder(fund.id)}
              >
                {t("assignRemainder")}
              </Button>
            ) : null}

            {/*
              The same masked money field the rest of finance records amounts with: locale separators, the
              currency's own decimal scale, no negatives. It was a bare `type='number'`, which meant this one
              screen accepted and displayed money differently from every other screen that takes money.
            */}
            <div className='w-40'>
              <CurrencyAmountInput
                id={`alloc-${fund.id}`}
                currencyCode={currency}
                value={draft[fund.id] ?? ""}
                onChange={(value) => setDraft((current) => ({ ...current, [fund.id]: value }))}
                disabled={!canEdit || !state.isEditable}
                placeholder='0'
                inputClassName='text-right tabular-nums'
              />
            </div>
          </div>
        ))}
      </div>

      {overAllocated ? (
        <HelperMessage tone='danger' size='md'>
          {t("overAllocated", { amount: formatMoney(fromCents(Math.abs(remainingCents)), currency) })}
        </HelperMessage>
      ) : null}


      {!state.isEditable ? (
        <HelperMessage tone='info' size='md'>
          {t("locked")}
        </HelperMessage>
      ) : null}

    </div>
  )
}

function Figure({
  label,
  value,
  tone = "default",
}: {
  label: string
  value: string
  tone?: "default" | "success" | "warning" | "destructive"
}) {
  const toneClass =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "destructive"
        ? "text-destructive"
        : tone === "warning"
          ? "text-amber-600 dark:text-amber-400"
          : ""

  return (
    <div className='bg-background px-4 py-3'>
      <p className='text-muted-foreground text-xs font-medium tracking-wide uppercase'>{label}</p>
      <p className={`mt-1 text-lg font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  )
}
